import { useRef, useState, useLayoutEffect } from 'react';

// ─── SlidingToggle — shared capsule toggle with a JS-measured sliding highlight ──
// Factored out of the Billing & Plan Monthly/Yearly cycle toggle so Settings.tsx's Billing Mode
// toggle could reuse the exact same component/CSS instead of a one-off style, per the request,
// rather than the two toggles drifting into separate implementations. Same capsule shape, same
// sliding highlight, same overshoot easing as the original.
//
// JS-measured (the original was a fixed 50% split, sized correctly only because both buttons were
// forced to equal width via flex:1) rather than a percentage split, so this also works correctly
// for options with different label lengths -- e.g. "Centralized — Org Admin manages" vs
// "Per-Facility — Manager manages", which the Billing Mode use case actually has.
export interface SlidingToggleOption<T extends string> {
  id: T;
  label: string;
}

export default function SlidingToggle<T extends string>({ options, value, onChange }: {
  options: SlidingToggleOption<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [highlight, setHighlight] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const el = btnRefs.current[value];
    if (el) setHighlight({ left: el.offsetLeft, width: el.offsetWidth });
  }, [value, options.length]);

  return (
    <div className="sliding-toggle">
      <div className="sliding-toggle-highlight" style={{ width: highlight.width, transform: `translateX(${highlight.left}px)` }} />
      {options.map((o) => (
        <button
          key={o.id}
          ref={(el) => { btnRefs.current[o.id] = el; }}
          className={`sliding-toggle-btn${o.id === value ? ' active' : ''}`}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
